import _ from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'slick-carousel/slick/slick.css';
import qs from 'qs';
import {
  getHotelRequest,
  setHotelDetailLoading,
  setHotelRateSearch,
} from '../../store/slices/hotel';
import HotelDetails from './HotelDetails';
import SkeletonCards from '../SkeletonCards';
import { setHotelTripSearchInputs, setSelectedSearchMode } from '../../store/slices/tripSearch';
import prioritizeHotelRates from '../../helpers/prioritizeHotelRates';
import checkUserRole from '../../helpers/selectors/checkUserRole';
import { UserRoles } from '../../constants';

const HotelPageInfo = () => {
  const router = useRouter();
  const query = qs.parse(router.asPath.split('?')[1]);
  const dispatch = useDispatch();
  const {
    checkInDate,
    checkOutDate,
    exactMatchRateCodesOnly,
    doShowCommissionableRatesOnly,
    showRefundableHotelRatesOnly,
  } = query;
  const { id } = router.query;

  const { travelOptions } = useSelector((state) => state.tripSearch.searchResult);
  const hotelRateCodes = useSelector((store) => store.user.hotelRateCodes);
  const { travelAgency, isLoggedIn } = useSelector((store) => store.user);
  const isEndCustomer = useSelector(checkUserRole(UserRoles.CUSTOMER));
  const hotelKey =
    travelOptions &&
    travelOptions[0] &&
    travelOptions[0].hotelItinerary &&
    travelOptions[0].hotelItinerary.hotelKey;
  const hotelDetails = useSelector((store) => store.hotel.hotelDetails[hotelKey]);
  const prioritizedHotelDetails = useMemo(
    () =>
      prioritizeHotelRates(hotelDetails, travelAgency, hotelRateCodes, isEndCustomer, isLoggedIn),
    [hotelDetails, travelAgency, hotelRateCodes, isEndCustomer, isLoggedIn],
  );
  const { isHotelDetailLoading } = useSelector((store) => store.hotel);
  const { hotel: hotelSearchInputs } = useSelector((store) => store.tripSearch.searchInputs);
  const exactMatchRateCodesOnlyValue = _.isNil(exactMatchRateCodesOnly)
    ? hotelSearchInputs.exactMatchRateCodesOnly
    : exactMatchRateCodesOnly;
  const doShowCommissionableRatesOnlyValue = _.isNil(doShowCommissionableRatesOnly)
    ? hotelSearchInputs.doShowCommissionableRatesOnly
    : doShowCommissionableRatesOnly;
  const showRefundableHotelRatesOnlyValue = _.isNil(showRefundableHotelRatesOnly)
    ? hotelSearchInputs.showRefundableHotelRatesOnly
    : showRefundableHotelRatesOnly;

  const rateCodesFromQuery = useMemo(() => {
    const parsedQuery = qs.parse(query) || {};
    return parsedQuery.rateCodes || [];
  }, [query]);

  useEffect(() => {
    if (!id) return;

    dispatch(setSelectedSearchMode('2'));
    dispatch(
      setHotelRateSearch(
        !_.isNil(checkInDate) && !_.isNil(checkOutDate) && checkInDate && checkOutDate,
      ),
    );

    dispatch(setHotelDetailLoading(true));

    dispatch(
      getHotelRequest({
        itinerary: {
          hotel: {
            ids: {
              sabreLodgingId: id,
            },
            checkInStartTime: checkInDate,
            checkOutEndTime: checkOutDate,
            rateCodes: rateCodesFromQuery,
            exactMatchRateCodesOnly: exactMatchRateCodesOnlyValue,
            doShowCommissionableRatesOnly: doShowCommissionableRatesOnlyValue,
            showRefundableHotelRatesOnly: showRefundableHotelRatesOnlyValue,
          },
        },
        isDedicatedHotelPage: true,
      }),
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let filteredRateCodes;
    if (rateCodesFromQuery?.length) {
      filteredRateCodes = rateCodesFromQuery;
    } else {
      filteredRateCodes = hotelRateCodes?.length
        ? hotelRateCodes
            .filter((rateCode) => rateCode.isSelectedByDefault)
            .map((rateCode) => rateCode.rateCode)
        : [];
    }

    if (filteredRateCodes?.length) {
      dispatch(setHotelTripSearchInputs({ rateCodes: filteredRateCodes }));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [hotelRateCodes, id]);

  const skeletonCards = useMemo(() => SkeletonCards(2, 'container mt-4'), []);

  if (isHotelDetailLoading) return skeletonCards;

  return <HotelDetails hotelDetails={prioritizedHotelDetails} />;
};
export default HotelPageInfo;
